import React, { useState } from 'react';
import { motion } from "framer-motion";
import {
  Github,
  Linkedin,
  Mail
} from "lucide-react";
import OmImage from "../../../data/Images/Om.jpg";
import AditeyImage from "../../../data/Images/Aditey.png";
import AmandeepImage from "../../../data/Images/Amandeep.jpg";

// Team Members Data
const teamMembers = [
  {
    id: "1",
    name: "Amandeep Singh",
    role: "Full Stack Developer",
    bio: "Passionate about building scalable web applications and community building.",
    avatar: AmandeepImage,
    skills: ["React", "Node.js", "MongoDB"],
    socials: {
      github: "https://github.com/amandeeep02",
      linkedin: "https://linkedin.com/in/amandeep-singh-rathod",
      email: "rathod.a@somaiya.edu"
    },
    color: "purple"
  },
  {
    id: "2",
    name: "Om Thanage",
    role: "Full Stack Developer",
    bio: "Focused on creating efficient and user-friendly digital experiences.",
    avatar: OmImage,
    skills: ["Express", "React", "MongoDB"],
    socials: {
      github: "https://github.com/Om-Thanage",
      linkedin: "https://www.linkedin.com/in/omthanage",
      email: "om.thanage@somaiya.edu"
    },
    color: "yellow"
  },
  {
    id: "3",
    name: "Aditey Kshirsagar",
    role: "Developer",
    bio: "Enthusiastic developer with a knack for problem solving and innovative solutions.",
    avatar: AditeyImage,
    skills: ["Java", "Python", "Web Dev"],

    socials: {
      github: "https://github.com/Aditey1908",
      linkedin: "https://www.linkedin.com/in/aditey-kshirsagar-0b7962307",
      email: "aditey.k@somaiya.edu"
    },
    color: "green"
  }
];

export function Team() {
  const [hoveredMember, setHoveredMember] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  // Helper colors maps
  const colors = {
    purple: { bar: 'bg-purple-500', badge: 'bg-purple-50 text-purple-700', hoverIcon: 'hover:bg-purple-50 hover:text-purple-600' },
    green: { bar: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700', hoverIcon: 'hover:bg-emerald-50 hover:text-emerald-600' },
    yellow: { bar: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700', hoverIcon: 'hover:bg-amber-50 hover:text-amber-600' },
  };

  return (
    <div className="min-h-screen font-sans">
      {/* Header */}
      <header className="py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl font-extrabold text-slate-800 tracking-tight">
              Meet The Team
            </h1>
          </motion.div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-2">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {teamMembers.map((member) => (
            <motion.div
              key={member.id}
              variants={cardVariants}
              onHoverStart={() => setHoveredMember(member.id)}
              onHoverEnd={() => setHoveredMember(null)}
              className="h-full"
            >
              <div
                className={`bg-white rounded-2xl overflow-hidden h-full border border-slate-200 flex flex-col transition-all duration-300 ${hoveredMember === member.id ? '-translate-y-2 shadow-xl' : 'shadow-md'
                  }`}
              >

                {/* Colored Top Section */}
                <div className={`h-1.5 w-full ${colors[member.color].bar}`} />

                <div className="p-8 flex flex-col items-center text-center gap-6 flex-grow">
                  {/* Profile Image */}
                  <div className="mb-2">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-md">
                      <img
                        src={member.avatar}
                        alt={member.name}
                        className={`w-full h-full object-cover transition-transform duration-300 ${hoveredMember === member.id ? 'scale-110' : ''}`}
                      />
                    </div>
                  </div>

                  {/* Name */}
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-slate-800">{member.name}</h3>
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{member.role}</p>
                  </div>

                  {/* Bio */}
                  <p className="text-slate-600 leading-relaxed text-[15px]">
                    {member.bio}
                  </p>

                  {/* Skills */}
                  <div className="w-full">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {member.skills.map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${colors[member.color].badge}`}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="flex gap-4 mt-auto pt-4">
                    {member.socials.email && (
                      <button
                        className={`p-2 rounded-lg text-slate-400 transition-all duration-200 ${colors[member.color].hoverIcon}`}
                        onClick={() => window.open(`mailto:${member.socials.email}`, '_blank')}
                      >
                        <Mail size={20} />
                      </button>
                    )}
                    {member.socials.github && (
                      <button
                        className={`p-2 rounded-lg text-slate-400 transition-all duration-200 ${colors[member.color].hoverIcon}`}
                        onClick={() => window.open(member.socials.github, '_blank')}
                      >
                        <Github size={20} />
                      </button>
                    )}
                    {member.socials.linkedin && (
                      <button
                        className={`p-2 rounded-lg text-slate-400 transition-all duration-200 ${colors[member.color].hoverIcon}`}
                        onClick={() => window.open(member.socials.linkedin, '_blank')}
                      >
                        <Linkedin size={20} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </main>

    </div>
  );
}

export default Team;
